import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from 'react-toastify';

import { detectors as detectorTemplates } from '../../detectors';
import { getActuatorNumberOfChannels } from '../../Functions/ActuatorChannels';
import { addPublicURL } from "../../Functions/AddPublicURL";
import { connectActuatorToDetector, disconnectActuatorFromDetector, getDetectorActuatorChannels, getDetectorConnectedActuatorIds, isActuatorConnectedToDetector, MAX_CONNECTED_ACTUATORS_PER_DETECTOR, normalizeDetectorConnections } from '../../Functions/DetectorConnections';
import { formatEntityId } from '../../Functions/FormatEntityId';
import { getIconPath } from '../../Functions/GetIconPath';
import { setActuatorControllerLinks, setDetectors, setShowDetectorDetail } from "../../redux/reducers/appReducer";
import { setSelectedDetector } from "../../redux/reducers/detectorsReducer";
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { ActuatorStateI } from '../../redux/types/app';
import Button from "../Button/Button";
import Checkbox from "../Checkbox/Checkbox";

import styles from "./DetectorDetail.module.scss";

const DetectorDetail: React.FC = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const selectedDetector = useAppSelector((state) => state.detectors.detector);
    const detectors = useAppSelector((state) => state.app.detectors);
    const actuators = useAppSelector((state) => state.app.actuators);
    const controllers = useAppSelector((state) => state.controllers.devices);
    const [expandedActuatorIds, setExpandedActuatorIds] = useState<number[]>([]);
    const detectorTemplate = detectorTemplates.find((template) =>
        template.name === selectedDetector.name || template.refImg === selectedDetector.refImg
    );
    const detectorName = selectedDetector.model ?? selectedDetector.name;
    const detectorImage = selectedDetector.refImg || detectorTemplate?.refImg || '';

    const syncDetector = (nextSelectedDetector: typeof selectedDetector) => {
        const detectorId = selectedDetector.id;
        if (detectorId === undefined) {
            return;
        }

        dispatch(setSelectedDetector(nextSelectedDetector));
        dispatch(
            setDetectors(
                detectors.map((detector) =>
                    detector.id === detectorId
                        ? nextSelectedDetector
                        : detector
                )
            )
        );
        dispatch(setActuatorControllerLinks(controllers));
    };

    const handleCheckboxClick = (actuatorId: number) => () => {
        const isConnected = isActuatorConnectedToDetector(selectedDetector, actuatorId);

        if (isConnected) {
            syncDetector(disconnectActuatorFromDetector(selectedDetector, actuatorId));
            setExpandedActuatorIds((current) => current.filter((id) => id !== actuatorId));
            return;
        }

        setExpandedActuatorIds((current) => current.includes(actuatorId)
            ? current.filter((id) => id !== actuatorId)
            : [...current, actuatorId]);
    };

    const handleChannelChange = (actuator: ActuatorStateI, channel: number, checked: boolean) => {
        if (actuator.id === undefined) {
            return;
        }
        const normalizedDetector = normalizeDetectorConnections(selectedDetector, actuators);
        const currentChannels = getDetectorActuatorChannels(normalizedDetector, actuator);
        const nextChannels = checked ? [...currentChannels, channel] : currentChannels.filter((value) => value !== channel);
        if (nextChannels.length === 0) {
            syncDetector(disconnectActuatorFromDetector(normalizedDetector, actuator.id));
            return;
        }
        const result = connectActuatorToDetector(normalizedDetector, actuator.id, nextChannels);
        if (!result.connected) {
            toast.error(t('detectorDetail.errorConnectionLimit', { count: MAX_CONNECTED_ACTUATORS_PER_DETECTOR }));
            return;
        }

        syncDetector(result.detector);
    };

    const connectedCount = getDetectorConnectedActuatorIds(selectedDetector).length;

    return (
        <>
            <div className={styles.DetectorDetail}>
                <div className={styles.DetectorDetail__wrapper}>
                    <h2>{t('detectorDetail.heading')}</h2>
                    <p>{t('detectorDetail.description')}</p>
                    <img src={getIconPath('arrow_back')} alt="Back" onClick={() => dispatch(setShowDetectorDetail(false))} className={styles.DetectorDetail__back} />

                    <div className={styles.DetectorDetail__container}>
                        <div className={styles.DetectorDetail__container__left}>
                            <div className={styles.DetectorDetail__container__left__actuators}>
                                <div className={styles.DetectorDetail__container__left__actuators__heading}>
                                    <p>{t('detectorDetail.actuators.select')}</p>
                                    <span></span>
                                    <p>{t('detectorDetail.actuators.id')}</p>
                                    <p>{t('detectorDetail.actuators.name')}</p>
                                    <p>{t('detectorDetail.actuators.status')}</p>
                                </div>
                                <div className={styles.DetectorDetail__container__left__actuators__list}>
                                    {actuators.map((actuator) => (
                                        (actuator.id !== undefined) && (actuator.applianceType !== 'hvac')  && (
                                            <div key={actuator.id} className={styles.DetectorDetail__container__left__actuators__item}>
                                                <div style={{ display: "flex", justifyContent: 'center' }}>
                                                    <Checkbox
                                                        checked={isActuatorConnectedToDetector(selectedDetector, actuator.id) || expandedActuatorIds.includes(actuator.id)}
                                                        onChange={handleCheckboxClick(actuator.id)}
                                                        variant="outline"
                                                    >
                                                    </Checkbox>
                                                </div>
                                                <img
                                                    className={styles.DetectorDetail__container__left__actuators__item__icon}
                                                    src={addPublicURL(`assets/actuators/${actuator.refImg}`)}
                                                    alt={actuator.type}
                                                    draggable={false}
                                                />
                                                <p>{formatEntityId('actuator', actuator.id)}</p>
                                                <p>{actuator.type} {actuator.name}</p>
                                                {actuator.state !== undefined && (
                                                    <img className={styles.DetectorDetail__container__left__actuators__item__state} src={getIconPath(actuator.state)} alt={actuator.state} />
                                                )}
                                                {(isActuatorConnectedToDetector(selectedDetector, actuator.id) || expandedActuatorIds.includes(actuator.id)) && (
                                                    <fieldset className={styles.DetectorDetail__channels}>
                                                        <legend>{t('detectorDetail.actuators.channels')}</legend>
                                                        {Array.from({ length: getActuatorNumberOfChannels(actuator) }, (_, index) => index + 1).map((channel) => (
                                                            <label key={channel}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={getDetectorActuatorChannels(selectedDetector, actuator).includes(channel)}
                                                                    onChange={(event) => handleChannelChange(actuator, channel, event.target.checked)}
                                                                />
                                                                {t('detectorDetail.actuators.channel')} {channel}
                                                            </label>
                                                        ))}
                                                    </fieldset>
                                                )}
                                            </div>
                                        )
                                    ))}
                                </div>
                            </div>
                            <div className={styles.DetectorDetail__container__left__save}>
                                <Button onClick={() => dispatch(setShowDetectorDetail(false))} size="lg" variant="fill">{t('apply')}</Button>
                            </div>
                        </div>
                        <span></span>
                        <div className={styles.DetectorDetail__container__detector}>
                            <div className={styles.DetectorDetail__container__detector__content}>
                                <img
                                    src={addPublicURL(`assets/detectors/${detectorImage}`)}
                                    alt={detectorName}
                                />
                            </div>
                            <div className={styles.DetectorDetail__container__detector__info}>
                                <h3>{detectorName} | {formatEntityId('detector', selectedDetector?.id ?? 0)}</h3>
                                <div style={{ marginTop: '1rem' }}>
                                    <h2>{t('detectorDetail.connectedActuators')}: {connectedCount}/{MAX_CONNECTED_ACTUATORS_PER_DETECTOR}</h2>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default DetectorDetail;
